import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, languages, t } = useLanguage();

  const navItems = [
    { path: '/', key: 'home' },
    { path: '/o-nas', key: 'about' },
    { path: '/sluzby', key: 'services' },
    { path: '/realizace', key: 'gallery' },
    { path: '/kontakt', key: 'contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const LanguageSwitcher = ({ onSelect }) => (
    <div className="flex items-center space-x-1" data-testid="lang-switcher">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => {
            setLanguage(lang.code);
            if (onSelect) onSelect();
          }}
          data-testid={`lang-btn-${lang.code}`}
          className={`px-2 py-1 text-xs font-bold rounded transition-all duration-200 ${
            language === lang.code
              ? 'bg-green-600 text-white dark:bg-green-500 dark:text-black'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
          }`}
          aria-label={`Switch to ${lang.name}`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-black/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            <img
              src="/logo.jpg"
              alt="Fabrikov Logo"
              className="w-10 h-10 rounded-lg transform group-hover:scale-105 transition-transform duration-200"
            />
            <span className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              FABRIKOV
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.key}`}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'text-green-600 dark:text-green-400 bg-green-600/10 dark:bg-green-400/10'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}

            {/* Language Switcher (Desktop) */}
            <div className="ml-3 pl-3 border-l border-gray-200 dark:border-gray-700">
              <LanguageSwitcher />
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-desktop"
              className="ml-2 p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </nav>

          {/* Mobile Buttons */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile"
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-toggle"
              className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-800">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${item.key}`}
                className={`block px-4 py-3 rounded-md text-base font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'text-green-600 dark:text-green-400 bg-green-600/10 dark:bg-green-400/10'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}

            {/* Language Switcher (Mobile) */}
            <div className="px-4 py-3 mt-2 border-t border-gray-200 dark:border-gray-700 flex justify-center">
              <LanguageSwitcher onSelect={() => setIsMenuOpen(false)} />
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
